import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { formatEuro } from '../lib/utils';
import { CartItem } from '../types/supabase';
import type { Product, ProductVariant } from '../types/product';

interface CartStore {
  items: CartItem[];
  /** Aggiunge `quantity` vasetti del formato, sommandoli a quelli già nel carrello. */
  addItem: (product: Product, variant: ProductVariant, quantity?: number) => void;
  removeItem: (variantId: string) => void;
  updateQuantity: (variantId: string, quantity: number) => void;
  clearCart: () => void;
  /**
   * Allinea il carrello al database: un carrello salvato può avere giorni e
   * nel frattempo prezzi e giacenze cambiano. Restituisce gli avvisi per il
   * cliente; se il database non risponde lascia tutto com'è.
   */
  syncCart: () => Promise<string[]>;
}

export const CART_STORAGE_KEY = 'apicoltura-cart';

const CART_VARIANT_SELECT = 'id, label, grams, price, stock, active, products(name, image_url, active)';

interface FreshVariant {
  id: string;
  label: string;
  grams: number;
  price: number;
  stock: number;
  active: boolean;
  products: { name: string; image_url: string | null; active: boolean } | null;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Il localStorage è modificabile da fuori: passa solo una riga ben formata. */
const isCartItem = (item: Partial<CartItem> | null): item is CartItem =>
  !!item &&
  typeof item.id === 'string' &&
  UUID.test(item.id) &&
  typeof item.productId === 'string' &&
  typeof item.name === 'string' &&
  typeof item.price === 'number' &&
  typeof item.grams === 'number' &&
  typeof item.stock === 'number' &&
  Number.isInteger(item.quantity) &&
  (item.quantity ?? 0) > 0;

// Una riga del carrello è un formato (variant): lo stesso miele in 250 g e
// 1 kg sono due righe distinte. Prezzo e giacenza qui sono solo indicativi:
// create-checkout-session li rilegge dal database.
// Il carrello resta nel browser (nessun dato personale, solo i prodotti scelti)
// e si svuota dalla pagina di conferma, dopo il pagamento.
export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
  items: [],
  addItem: (product, variant, quantity = 1) =>
    set((state) => {
      const existingItem = state.items.find((item) => item.id === variant.id);
      if (existingItem) {
        return {
          items: state.items.map((item) =>
            item.id === variant.id
              ? { ...item, quantity: Math.min(item.quantity + quantity, variant.stock), stock: variant.stock }
              : item
          ),
        };
      }
      return {
        items: [
          ...state.items,
          {
            id: variant.id,
            productId: product.id,
            name: product.name,
            variantLabel: variant.label,
            grams: variant.grams,
            price: variant.price,
            image: product.image_url ?? '',
            quantity: Math.min(quantity, variant.stock),
            stock: variant.stock,
          },
        ],
      };
    }),
  removeItem: (variantId) =>
    set((state) => ({
      items: state.items.filter((item) => item.id !== variantId),
    })),
  updateQuantity: (variantId, quantity) =>
    set((state) => {
      if (quantity <= 0) {
        return {
          items: state.items.filter((item) => item.id !== variantId),
        };
      }
      return {
        items: state.items.map((item) =>
          item.id === variantId ? { ...item, quantity: Math.min(quantity, item.stock) } : item
        ),
      };
    }),
  clearCart: () => set({ items: [] }),
  syncCart: async () => {
    const ids = get().items.map((item) => item.id);
    if (ids.length === 0) return [];
    const { data, error } = await supabase.from('product_variants').select(CART_VARIANT_SELECT).in('id', ids);
    if (error || !data) {
      console.error('Error syncing cart:', error);
      return [];
    }
    const fresh = new Map((data as unknown as FreshVariant[]).map((v) => [v.id, v]));
    const notices: string[] = [];
    set((state) => ({
      items: state.items.flatMap((item) => {
        // Aggiunto mentre la richiesta era in corso: è già aggiornato.
        if (!ids.includes(item.id)) return [item];
        const variant = fresh.get(item.id);
        if (!variant || !variant.active || !variant.products?.active) {
          notices.push(`${item.name} (${item.variantLabel}) non è più in vendita: l'abbiamo tolto dal carrello.`);
          return [];
        }
        if (variant.stock <= 0) {
          notices.push(`${item.name} (${item.variantLabel}) è esaurito: l'abbiamo tolto dal carrello.`);
          return [];
        }
        if (variant.price !== item.price) {
          notices.push(`Il prezzo di ${item.name} (${item.variantLabel}) è cambiato: ora è ${formatEuro(variant.price)}.`);
        }
        const quantity = Math.min(item.quantity, variant.stock);
        if (quantity < item.quantity) {
          notices.push(`${item.name} (${item.variantLabel}): ne restano ${variant.stock}, abbiamo aggiornato la quantità.`);
        }
        return [
          {
            ...item,
            name: variant.products.name,
            variantLabel: variant.label,
            grams: variant.grams,
            price: variant.price,
            image: variant.products.image_url ?? '',
            quantity,
            stock: variant.stock,
          },
        ];
      }),
    }));
    return notices;
  },
    }),
    {
      name: CART_STORAGE_KEY,
      version: 1,
      partialize: (state) => ({ items: state.items }),
      merge: (saved, current) => {
        const items = (saved as { items?: unknown } | undefined)?.items;
        return { ...current, items: Array.isArray(items) ? items.filter(isCartItem) : [] };
      },
    }
  )
);
